import React, { useState, useEffect, useCallback } from 'react';
import { 
  getCommentsForQuestion, 
  getRepliesForComment, 
  addComment,
  deleteComment,
  deleteReply,
  testDirectCommentWrite,
  clearAllComments,
  isUserAdmin,
  getUserProfile,
  checkUserCanComment
} from '../services/questionsService';
import { isUserBlocked } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import Comment from './Comment';
import CommentFormWithMentions from './CommentFormWithMentions';
import { trackInteraction } from '../services/recommendationService';

function CommentSection({ questionId, onCommentCountChange, isLeaderboard = false }) {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isUserAdminState, setIsUserAdminState] = useState(false);
  const [currentUserPhoto, setCurrentUserPhoto] = useState(null);
  const [isUserBlockedState, setIsUserBlockedState] = useState(false);
  const commentsPerPage = 10;
  const commentsEndRef = React.useRef(null);

  // Function to check if this is a leaderboard question
  const isLeaderboardQuestion = () => {
    // First check prop from parent component (this is the most reliable)
    if (isLeaderboard) {
      console.log('Question is leaderboard type from direct prop');
      return true;
    }

    // Fallback: check cached question data from localStorage
    const cachedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
    const questionData = cachedQuestions[questionId];

    // Debug log so we can see what's happening
    console.log('Checking if leaderboard question:', {
      questionId,
      questionData,
      propIsLeaderboard: isLeaderboard,
      cachedIsLeaderboard: questionData && questionData.questionType === 'leaderboard',
      cachedQuestionsKeys: Object.keys(cachedQuestions)
    });

    // Check if this question is a leaderboard type
    return isLeaderboard || (questionData && questionData.questionType === 'leaderboard');
  };

  // Debug log on mount
  useEffect(() => {
    console.log('CommentSection for question ID:', questionId);
    console.log('Is leaderboard question:', isLeaderboardQuestion());
    console.log('Direct isLeaderboard prop:', isLeaderboard);

    // Check the cached questions in localStorage
    const cachedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
    console.log('Cached questions:', cachedQuestions);
  }, [questionId, isLeaderboard]);
  
  // Check if user has voted on this question - account for user-specific votes
  const hasVotedOnQuestion = () => {
    if (currentUser) {
      // User-specific storage key for logged-in users
      const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
      const votedQuestions = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
      
      const hasVoted = votedQuestions[questionId] !== undefined;
      console.log(`CommentSection: User ${currentUser.uid} vote check for question ${questionId}: ${hasVoted}`);
      
      return hasVoted;
    } else {
      // Anonymous-only storage - completely separate from logged-in users
      const anonVotedQuestionsKey = `votedQuestions_anonymous`;
      const votedQuestions = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
      
      const hasVoted = votedQuestions[questionId] !== undefined;
      console.log(`CommentSection: Anonymous user vote check for question ${questionId}: ${hasVoted}`);
      
      return hasVoted;
    }
  };
  
  // Get which option (A or B) the user voted for - account for user-specific options
  const getUserVoteOption = () => {
    if (currentUser) {
      // User-specific storage key for logged-in users
      const userVoteOptionsKey = `userVoteOptions_${currentUser.uid}`;
      const userVoteOptions = JSON.parse(localStorage.getItem(userVoteOptionsKey) || '{}');
      
      return userVoteOptions[questionId];
    } else {
      // Anonymous-only storage - completely separate from logged-in users
      const anonVoteOptionsKey = `userVoteOptions_anonymous`;
      const userVoteOptions = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
      
      return userVoteOptions[questionId];
    }
  };
  
  // Function to perform a fresh admin check
  const checkIsUserAdmin = useCallback(() => {
    if (!currentUser) return false;
    const admin = isUserAdmin(currentUser.uid);
    console.log('Fresh admin check for user:', currentUser.uid, 'Result:', admin);
    return admin;
  }, [currentUser]);
  
  // For debugging and setup
  useEffect(() => {
    console.log('CommentSection mounted/updated, questionId:', questionId);
    console.log('Current user:', currentUser);
    console.log('hasVotedOnQuestion:', hasVotedOnQuestion());
    console.log('getUserVoteOption:', getUserVoteOption());
    
    // Check if current user is admin
    if (currentUser) {
      const admin = checkIsUserAdmin();
      setIsUserAdminState(admin);
      console.log('Is user admin:', admin);
      
      // Check if the current user is blocked
      const checkBlockStatus = async () => {
        try {
          const blocked = await isUserBlocked(currentUser.uid);
          setIsUserBlockedState(blocked);
          console.log('Is user blocked:', blocked);
        } catch (error) {
          console.error('Error checking block status:', error);
          setIsUserBlockedState(false);
        }
      };
      
      // Fetch current user's profile photo
      const fetchUserProfile = async () => {
        try {
          const profile = await getUserProfile(currentUser.uid);
          if (profile && profile.photoURL) {
            setCurrentUserPhoto(profile.photoURL);
          } else {
            setCurrentUserPhoto(currentUser.photoURL);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setCurrentUserPhoto(currentUser.photoURL);
        }
      };
      
      checkBlockStatus();
      fetchUserProfile();
    }
    
    return () => console.log('CommentSection unmounting');
  }, [questionId, currentUser]);

  // Reset state when question changes
  useEffect(() => {
    console.log('Question changed, resetting comment state for question:', questionId);
    // Reset all state when question changes
    setComments([]);
    setNewComment('');
    setReplyToId(null);
    setReplyText('');
    setLoading(true);
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    
    const fetchInitialComments = async () => {
      setLoading(true);
      
      // Add retry logic for fetching comments
      let retries = 3;
      let questionComments = null;
      
      while (retries > 0 && !questionComments) {
        try {
          console.log(`Attempt ${4-retries}: Fetching comments for question ${questionId}`);
          questionComments = await getCommentsForQuestion(questionId, 1, commentsPerPage);
          
          if (!questionComments || !questionComments.comments) {
            console.error('No comments returned from Firestore');
            retries--;
            if (retries <= 0) throw new Error('Failed to fetch comments after multiple attempts');
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
          
          console.log(`Retrieved ${questionComments.comments.length} comments for question ${questionId}`);
          
          // Fetch replies for each comment
          const commentsWithReplies = await Promise.all(
            questionComments.comments.map(async (comment) => {
              console.log(`Fetching replies for comment ${comment.id}`);
              const replies = await getRepliesForComment(comment.id);
              console.log(`Retrieved ${replies.length} replies for comment ${comment.id}`);
              return { ...comment, replies };
            })
          );
          
          console.log('Setting comments in state:', commentsWithReplies);
          setComments(commentsWithReplies);
          setHasMore(questionComments.hasMore);
          
          // Update parent component with comment count if callback provided
          if (onCommentCountChange) {
            onCommentCountChange(questionComments.totalCount);
          }
          
          break;
        } catch (error) {
          console.error('Error fetching comments:', error);
          retries--;
          if (retries <= 0) {
            // After all retries, set an empty array to avoid loading state
            setComments([]);
            setHasMore(false);
          } else {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      setLoading(false);
    };
    
    if (questionId) {
      // Use a short timeout to ensure smooth transitions between questions
      const timer = setTimeout(() => {
        fetchInitialComments();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [questionId, onCommentCountChange, commentsPerPage]);
  
  // Function to refresh comments (for use after adding/deleting comments)
  const fetchComments = useCallback(async () => {
    if (!questionId) return;
    
    try {
      console.log(`Refreshing comments for question ${questionId}`);
      const questionComments = await getCommentsForQuestion(questionId, 1, commentsPerPage);
      
      if (!questionComments || !questionComments.comments) {
        console.error('No comments returned when refreshing');
        return;
      }
      
      // Fetch replies for each comment
      const commentsWithReplies = await Promise.all(
        questionComments.comments.map(async (comment) => {
          const replies = await getRepliesForComment(comment.id);
          return { ...comment, replies };
        })
      );
      
      setComments(commentsWithReplies);
      setPage(1); // Reset to first page
      setHasMore(questionComments.hasMore);
      
      // Update parent component with comment count if callback provided
      if (onCommentCountChange) {
        onCommentCountChange(questionComments.totalCount);
      }
    } catch (error) {
      console.error('Error refreshing comments:', error);
    }
  }, [questionId, commentsPerPage, onCommentCountChange]);

  // Fetch more comments when scrolling
  const fetchMoreComments = async () => {
    if (!hasMore || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const moreComments = await getCommentsForQuestion(questionId, nextPage, commentsPerPage);
      
      // Fetch replies for each new comment
      const moreCommentsWithReplies = await Promise.all(
        moreComments.comments.map(async (comment) => {
          const replies = await getRepliesForComment(comment.id);
          return { ...comment, replies };
        })
      );
      
      setComments(prevComments => [...prevComments, ...moreCommentsWithReplies]);
      setPage(nextPage);
      setHasMore(moreComments.hasMore);
    } catch (error) {
      console.error('Error fetching more comments:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  // Infinite scroll handler
  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    
    // Load more comments when user scrolls to bottom (with a buffer of 200px)
    if (scrollHeight - scrollTop <= clientHeight + 200 && hasMore && !loadingMore) {
      fetchMoreComments();
    }
  };

  // Handle submitting a new comment
  const handleSubmitComment = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Require user to be logged in
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('openLoginModal'));
      return;
    }

    // For standard questions, require voting first
    // For leaderboard questions, allow commenting without voting
    if (!isLeaderboardQuestion() && !hasVotedOnQuestion()) {
      alert('You must vote on this question before commenting');
      return;
    }
    
    // Check if the user is blocked
    try {
      const isBlocked = await isUserBlocked(currentUser.uid);
      if (isBlocked) {
        alert('Sorry, you are unable to post comments at this time.');
        return;
      }
    } catch (error) {
      console.error('Error checking user block status:', error);
      // Continue as we don't want to block legitimate users due to error
    }
    
    if (!newComment || !newComment.trim()) return;
    
    // Client-side validation for comment length
    if (newComment.trim().length > 1000) {
      alert('Comment is too long (maximum 1000 characters)');
      return;
    }
    
    // Save the comment text for the entire function scope
    const savedCommentText = newComment;
    // Clear the input field after saving the text
    setNewComment('');
    
    // DON'T create a temporary comment right away
    // We'll handle that after the server confirms the comment was saved
    
    try {
      // Get user's vote option to save with the comment
      const userVoteOption = getUserVoteOption();
      
      console.log('Submitting comment:', {
        questionId,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'User',
        text: savedCommentText,
        option: userVoteOption
      });
      
      // Make sure we have a valid user ID
      const userId = currentUser?.uid || 'anonymous_' + Date.now();
      const userName = currentUser?.displayName || 'User';
      
      // Submit the comment to Firebase with retry logic
      console.log('Submitting to Firebase directly...');
      let retries = 3;
      let commentResult = null;
      
      while (retries > 0 && !commentResult) {
        try {
          // Add userVoteOption to the comment data
          commentResult = await addComment(
            questionId, 
            userId, 
            userName, 
            savedCommentText, 
            null, // parentId (null for top-level comments)
            userVoteOption // Pass the vote option to store with the comment
          );
          console.log('Comment saved successfully on try #' + (4 - retries));
          break;
        } catch (err) {
          retries--;
          console.error(`Comment save attempt failed, ${retries} retries left:`, err);
          if (retries <= 0) throw err;
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      console.log('Comment successfully added with result:', commentResult);
      
      // Track comment interaction
      if (currentUser) {
        // Get question category from cached data
        const cachedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
        const questionData = cachedQuestions[questionId];
        const category = questionData?.category || null;
        
        await trackInteraction(currentUser.uid, questionId, 'comment', category);
      }
      
      // Add the new comment directly
      if (commentResult) {
        // Add the new comment to the top of the list
        setComments(prevComments => [
          { ...commentResult, replies: [] },
          ...prevComments
        ]);
      
        // Update parent component with new comment count
        if (onCommentCountChange) {
          onCommentCountChange(comments.length + 1);
        }
        
        // Scroll to the top to see the new comment
        if (commentsEndRef.current) {
          commentsEndRef.current.scrollTop = 0;
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      
      // Restore the comment text so user doesn't lose their input on network errors
      setNewComment(savedCommentText);
      
      // Show a user-friendly error message based on the error type
      const errorMessage = error.message || 'Failed to add your comment. Please try again.';
      alert(errorMessage);
      
      // Log analytics or monitoring events about the failure for debugging
      console.log('Comment submission failed:', {
        error: error.message,
        code: error.code,
        questionId,
        userId: currentUser?.uid
      });
    }
  };

  // Handle submitting a reply
  const handleSubmitReply = async (e, commentId) => {
    e.preventDefault();
    
    // Require user to be logged in and to have voted on this question
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('openLoginModal'));
      return;
    }
    
    if (!hasVotedOnQuestion()) {
      alert('You must vote on this question before replying');
      return;
    }
    
    // Check if the user is blocked
    try {
      const isBlocked = await isUserBlocked(currentUser.uid);
      if (isBlocked) {
        alert('Sorry, you are unable to post replies at this time.');
        return;
      }
    } catch (error) {
      console.error('Error checking user block status:', error);
      // Continue as we don't want to block legitimate users due to error
    }
    
    if (!replyText.trim()) return;
    
    // Client-side validation for reply length
    if (replyText.trim().length > 500) {
      alert('Reply is too long (maximum 500 characters)');
      return;
    }
    
    // Save the reply text for the entire function scope
    const savedReplyText = replyText;
    // Clear the reply input after saving the text
    setReplyText('');
    
    // Generate a unique ID for the temporary reply
    const tempId = 'temp-reply-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
    
    // Create a temporary reply for optimistic UI
    const tempReply = {
      id: tempId,
      questionId,
      userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email || 'User',
      text: savedReplyText,
      parentId: commentId,
      // Use ISO string for immediate display
      localCreatedAt: new Date().toISOString(),
      // This will be overwritten by the server
      createdAt: {
        toDate: () => new Date()
      },
      userPhotoURL: currentUserPhoto,
      isOptimistic: true // Mark as optimistic update
    };
    
    // Add temp reply to UI
    setComments(prevComments => 
      prevComments.map(comment => 
        comment.id === commentId 
          ? { 
              ...comment, 
              replies: [...(comment.replies || []), tempReply] 
            } 
          : comment
      )
    );
    
    try {
      console.log('Submitting reply:', {
        questionId,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'User',
        text: savedReplyText,
        parentId: commentId
      });
      
      // Submit to Firebase directly with retry logic
      console.log('Submitting reply to Firebase directly...');
      let retries = 3;
      let replyResult = null;
      
      while (retries > 0 && !replyResult) {
        try {
          replyResult = await addComment(
            questionId,
            currentUser.uid,
            currentUser.displayName || 'User',
            savedReplyText,
            commentId
          );
          console.log('Reply saved successfully on try #' + (4 - retries));
          break;
        } catch (err) {
          retries--;
          console.error(`Reply save attempt failed, ${retries} retries left:`, err);
          if (retries <= 0) throw err;
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      console.log('Reply successfully added with result:', replyResult);
      
      // Update the optimistic reply with the real data
      if (replyResult) {
        setComments(prevComments => 
          prevComments.map(comment => 
            comment.id === commentId 
              ? { 
                  ...comment, 
                  replies: (comment.replies || []).map(reply => 
                    reply.id === tempId 
                      ? replyResult
                      : reply
                  )
                } 
              : comment
          )
        );
      }
      
      // Close the reply form after successful submission
      setReplyToId(null);
      
    } catch (error) {
      console.error('Error adding reply:', error);
      
      // Remove the temporary reply from the UI if the request failed
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === commentId 
            ? { 
                ...comment, 
                replies: (comment.replies || []).filter(r => !r.id.startsWith('temp-'))
              } 
            : comment
        )
      );
      
      // If failed, restore the reply text so user doesn't lose their input
      setReplyText(savedReplyText);
      
      // Show a user-friendly error message based on the error type
      const errorMessage = error.message || 'Failed to add your reply. Please try again.';
      alert(errorMessage);
      
      // Log the error for debugging
      console.log('Reply submission failed:', {
        error: error.message,
        code: error.code,
        questionId,
        commentId,
        userId: currentUser?.uid
      });
    }
  };

  // Format timestamp with better error handling
  const formatDate = (timestamp) => {
    // For debugging
    console.log('Formatting timestamp:', timestamp);
    
    if (!timestamp) return 'Just now';
    
    try {
      // Handle different timestamp formats: Firebase Timestamp, ISO string, or Date object
      let date;
      
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Firebase Timestamp object
        console.log('Detected Firebase timestamp');
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        // ISO string
        console.log('Detected ISO string timestamp');
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        // Already a Date object
        console.log('Detected Date object timestamp');
        date = timestamp;
      } else if (timestamp.seconds && timestamp.nanoseconds) {
        // Firestore Timestamp in object form
        console.log('Detected Firestore timestamp object');
        date = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000);
      } else if (timestamp.localCreatedAt) {
        // Use the local timestamp if available (for optimistic updates)
        console.log('Using localCreatedAt timestamp');
        date = new Date(timestamp.localCreatedAt);
      } else {
        // Unknown format, use current date as fallback
        console.warn('Unknown timestamp format:', timestamp);
        date = new Date();
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date encountered:', timestamp);
        return 'Just now';
      }
      
      // For very recent comments (less than 1 minute ago)
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      
      // If the diffMs is negative (future date), it's probably a new comment
      if (diffMs < 0 || diffMs < 60000) {
        return 'Just now';
      }
      
      // For comments less than 1 hour old
      if (diffMs < 3600000) {
        const minutes = Math.floor(diffMs / 60000);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      }
      
      // For today's comments
      if (date.toDateString() === now.toDateString()) {
        return `Today at ${date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      
      // For all other comments
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Just now';
    }
  };

  if (loading) {
    return <div className="comments-loading">Loading comments...</div>;
  }


  // Get the appropriate color for comment styling based on user vote
  const getCommentBorderColor = (userId) => {
    // Set colors to match the voting buttons exactly
    const DEFAULT_COLOR = '#4a90e2';
    const OPTION_A_COLOR = 'white'; // Option A (white button)
    const OPTION_B_COLOR = 'black'; // Option B (black button)
    
    // Helper to get the color based on option
    const getColorForOption = (option) => {
      if (option === 'A') {
        try {
          const storedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
          const thisQuestion = storedQuestions[questionId];
          if (thisQuestion?.styles?.optionABackgroundColor) {
            return thisQuestion.styles.optionABackgroundColor;
          }
        } catch (e) {
          console.warn('Could not get question styling from cache');
        }
        return OPTION_A_COLOR;
      } 
      else if (option === 'B') {
        try {
          const storedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
          const thisQuestion = storedQuestions[questionId];
          if (thisQuestion?.styles?.optionBBackgroundColor) {
            return thisQuestion.styles.optionBBackgroundColor;
          }
        } catch (e) {
          console.warn('Could not get question styling from cache');
        }
        return OPTION_B_COLOR;
      }
      return DEFAULT_COLOR;
    };
    
    try {
      // Check if the comment's user ID is the current logged-in user
      if (currentUser && currentUser.uid === userId) {
        // Get current user's vote option for this question using our updated function
        const option = getUserVoteOption();
        if (option) {
          return getColorForOption(option);
        }
      } 
      // If this is an anonymous user's comment and we are anonymous
      else if (!currentUser && userId.startsWith('anonymous_')) {
        // Use the anonymous vote option
        const anonVoteOptionsKey = `userVoteOptions_anonymous`;
        const userVoteOptions = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
        const option = userVoteOptions[questionId];
        if (option) {
          return getColorForOption(option);
        }
      }
      
      // Check if we have the option stored in the comment object itself
      const commentObj = comments.find(c => c.userId === userId);
      if (commentObj?.option) {
        return getColorForOption(commentObj.option);
      }
      
      // Try to lookup the user's vote from our userVotesCache
      const userVotesCache = JSON.parse(localStorage.getItem('userVotesCache') || '{}');
      if (userVotesCache[questionId] && userVotesCache[questionId][userId]) {
        const userOption = userVotesCache[questionId][userId];
        return getColorForOption(userOption);
      }
    } catch (e) {
      console.error('Error determining comment border color:', e);
    }
    
    // Last resort - return a color based on user ID to ensure consistency
    const lastCharCode = userId.charCodeAt(userId.length - 1) || 0;
    
    // This creates a relatively even distribution between A and B options
    if (lastCharCode % 2 === 0) {
      return OPTION_A_COLOR;
    } else {
      return OPTION_B_COLOR;
    }
  };
  
  // Handle deleting a comment
  const handleDeleteComment = async (commentId) => {
    if (!currentUser) return;
    
    try {
      // Always do a fresh check of admin status
      const freshAdminCheck = checkIsUserAdmin();
      console.log('Fresh admin check result for delete comment:', freshAdminCheck);
      
      // Find the comment to delete
      const commentToDelete = comments.find(c => c.id === commentId);
      
      // Check if the current user has permission to delete this comment
      if (!commentToDelete) {
        console.error('Comment not found:', commentId);
        return;
      }
      
      // Check permissions - only allow if user is admin or comment owner
      const isCommentOwner = commentToDelete.userId === currentUser.uid;
      if (!isCommentOwner && !freshAdminCheck) {
        console.error('Permission denied: User is not the comment owner or admin');
        alert('You do not have permission to delete this comment');
        return;
      }
      
      console.log('Deleting comment:', commentId, 'isOwner:', isCommentOwner, 'isAdmin:', freshAdminCheck);
      
      // Generate a temporary ID for the deleted comment to show optimistic UI update
      const deletedCommentId = `deleted-${commentId}`;
      const originalText = commentToDelete.text || '';
      
      // Optimistically remove the comment from UI
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === commentId
            ? { ...comment, id: deletedCommentId, isDeleted: true, originalText, text: 'Deleting comment...' }
            : comment
        )
      );
      
      // Call the API to delete the comment
      await deleteComment(commentId, questionId, currentUser.uid, freshAdminCheck);
      
      // Remove the comment from the UI completely on successful deletion
      setComments(prevComments => 
        prevComments.filter(comment => comment.id !== deletedCommentId)
      );
      
      // Update comment count if needed
      if (onCommentCountChange) {
        onCommentCountChange(comments.length - 1);
      }
      
      console.log('Comment successfully deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      
      // Restore the comment if deletion failed
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === `deleted-${commentId}`
            ? { ...comment, id: commentId, isDeleted: false, text: comment.originalText || comment.text }
            : comment
        )
      );
      
      alert(error.message || 'Failed to delete comment. Please try again.');
    }
  };
  
  // Handle deleting a reply
  const handleDeleteReply = async (replyId, parentId) => {
    if (!currentUser) return;
    
    try {
      // Always do a fresh check of admin status
      const freshAdminCheck = checkIsUserAdmin();
      console.log('Fresh admin check result for delete reply:', freshAdminCheck);
      
      // Find the parent comment and reply to delete
      const parentComment = comments.find(c => c.id === parentId);
      if (!parentComment || !parentComment.replies) {
        console.error('Parent comment not found:', parentId);
        return;
      }
      
      const replyToDelete = parentComment.replies.find(r => r.id === replyId);
      if (!replyToDelete) {
        console.error('Reply not found:', replyId);
        return;
      }
      
      // Check permissions - only allow if user is admin or reply owner
      const isReplyOwner = replyToDelete.userId === currentUser.uid;
      if (!isReplyOwner && !freshAdminCheck) {
        console.error('Permission denied: User is not the reply owner or admin');
        alert('You do not have permission to delete this reply');
        return;
      }
      
      console.log('Deleting reply:', replyId, 'from parent:', parentId, 'isOwner:', isReplyOwner, 'isAdmin:', freshAdminCheck);
      
      // Generate a temporary ID for the deleted reply to show optimistic UI update
      const deletedReplyId = `deleted-${replyId}`;
      const originalText = replyToDelete.text || '';
      
      // Optimistically update the UI to show reply as deleting
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === parentId
            ? {
                ...comment,
                replies: (comment.replies || []).map(reply =>
                  reply.id === replyId
                    ? { 
                        ...reply, 
                        id: deletedReplyId, 
                        isDeleted: true, 
                        originalText,
                        text: 'Deleting reply...' 
                      }
                    : reply
                )
              }
            : comment
        )
      );
      
      // Call the API to delete the reply
      await deleteReply(replyId, parentId, currentUser.uid, freshAdminCheck);
      
      // Remove the reply from the UI completely on successful deletion
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === parentId
            ? {
                ...comment,
                replies: (comment.replies || []).filter(reply => reply.id !== deletedReplyId)
              }
            : comment
        )
      );
      
      console.log('Reply successfully deleted');
    } catch (error) {
      console.error('Error deleting reply:', error);
      
      // Restore the reply if deletion failed
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === parentId
            ? {
                ...comment,
                replies: (comment.replies || []).map(reply =>
                  reply.id === `deleted-${replyId}`
                    ? { 
                        ...reply, 
                        id: replyId, 
                        isDeleted: false, 
                        text: reply.originalText || reply.text 
                      }
                    : reply
                )
              }
            : comment
        )
      );
      
      alert(error.message || 'Failed to delete reply. Please try again.');
    }
  };
  
  return (
    <div className="comments-container">
      {/* Scrollable comments area */}
      <div 
        className="comments-scroll-container" 
        onScroll={handleScroll}
        ref={commentsEndRef}
      >
        {comments.length === 0 ? (
          <div className="no-comments">
            {hasVotedOnQuestion() || isLeaderboardQuestion()
              ? "No comments yet. Be the first to share your thoughts!"
              : "Vote to join the conversation!"}
          </div>
        ) : (
          <>
            {comments.filter(comment => !comment.isDeleted).map(comment => (
              <Comment
                key={comment.id}
                comment={comment}
                onReply={(text, parentId) => {
                  setReplyText(text);
                  handleSubmitReply({ preventDefault: () => {} }, parentId);
                }}
                onDelete={handleDeleteComment}
                onDeleteReply={handleDeleteReply}
                currentUser={currentUser}
                isAdmin={checkIsUserAdmin()} // Always use a fresh admin check
                getCommentBorderColor={getCommentBorderColor}
              />
            ))}
              
            {loadingMore && (
              <div className="loading-more">Loading more comments...</div>
            )}
          </>
        )}
      </div>
      
      {/* Fixed Comment Input */}
      <div className="fixed-comment-form-container">
        {/* Allow comments from logged-in users who either voted or if it's a leaderboard question */}
        {currentUser && !currentUser.isAnonymous && (hasVotedOnQuestion() || isLeaderboardQuestion()) ? (
          isUserBlockedState ? (
            <div
              className="comment-login-message"
              style={{
                backgroundColor: '#fff8e1',
                padding: '15px',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '0.95rem',
                color: '#ff6f00',
                fontWeight: 500,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              Your account is currently blocked from posting comments
            </div>
          ) : (
            <CommentFormWithMentions
              questionId={questionId}
              onSubmit={async (text, parentId) => {
                // Create a custom submission handler for mentions
                if (!currentUser) {
                  window.dispatchEvent(new CustomEvent('openLoginModal'));
                  return;
                }

                // For standard questions, require voting first
                if (!isLeaderboardQuestion() && !hasVotedOnQuestion()) {
                  alert('You must vote on this question before commenting');
                  return;
                }
                
                // Check if the user is blocked
                try {
                  const isBlocked = await isUserBlocked(currentUser.uid);
                  if (isBlocked) {
                    alert('Sorry, you are unable to post comments at this time.');
                    return;
                  }
                } catch (error) {
                  console.error('Error checking user block status:', error);
                }
                
                if (!text || !text.trim()) return;
                
                if (text.trim().length > 1000) {
                  alert('Comment is too long (maximum 1000 characters)');
                  return;
                }
                
                try {
                  const userVoteOption = getUserVoteOption();
                  const userName = currentUser.displayName || 'User';
                  
                  const result = await addComment(
                    questionId,
                    currentUser.uid,
                    userName,
                    text.trim(),
                    null
                  );
                  
                  // Refresh comments
                  fetchComments();
                  
                  return result;
                } catch (error) {
                  console.error('Error submitting comment:', error);
                  alert('Failed to submit comment. Please try again.');
                }
              }}
              userVoteOption={isLeaderboardQuestion() ? 'leaderboard' : getUserVoteOption()}
              contextType="comment"
            />
          )
        ) : currentUser && !currentUser.isAnonymous ? (
          <div
            className="comment-login-message"
            style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '0.95rem',
              color: '#FF6B00',
              fontWeight: 500,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            You must vote on this question to comment
          </div>
        ) : (
          <div
            className="comment-login-message"
            onClick={() => window.dispatchEvent(new CustomEvent('openLoginModal'))}
            style={{
              backgroundColor: '#f0f2f5',
              padding: '15px',
              borderRadius: '24px',
              textAlign: 'center',
              fontSize: '1rem',
              color: '#FF6B00',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              margin: '0.5rem 0',
              transition: 'all 0.3s ease'
            }}
          >
            Please log in and vote to join the conversation
          </div>
        )}
      </div>
    </div>
  );
}

export default CommentSection;